// Shared analysis for the parsers (Apple 2703(d), phone records, Snapchat,
// Facebook, Instagram). Everything works from a list of "events":
//
//   { t: epoch ms, account: who the record belongs to, kind, source: sheet/label,
//     activity: true if it's something the account did, ips: [..], handle: the
//     other party (phone/email/username) if any, service?, places?: [cell id..],
//     detail: short text }
//
// The functions here only count and compare; every inference ("likely home")
// comes with its evidence so the investigator can judge it. Nothing leaves the browser.

// ─── Values ──────────────────────────────────────────────────────────────────

const RE_IPV4 = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;
export function isIp(v) {
  const s = String(v || '').trim();
  if (RE_IPV4.test(s)) return true;
  return /^[0-9a-f:.]{2,45}$/i.test(s) && (s.includes('::') || (s.match(/:/g) || []).length >= 5);
}

// "tel:+15551234567" / "[mailto:a@b.com]" → "+15551234567" / "a@b.com"
export function cleanHandle(v) {
  return String(v || '').trim().replace(/^\[|\]$/g, '').replace(/^(tel|mailto|sms|im|token):/i, '');
}

const digits = s => String(s || '').replace(/\D/g, '');
export const phoneKey = s => digits(s).replace(/^1(?=\d{10}$)/, '');

// One key per person however the handle is written: emails lower-cased, US
// phone numbers as 10 digits (+1 (555) 010-0001 = 15550100001 = 5550100001).
export function handleKey(h) {
  const s = cleanHandle(h).toLowerCase();
  if (s.includes('@')) return s;
  return /^\+?[\d\s().-]{7,}$/.test(s) ? phoneKey(s) : s;
}

// ─── Times ───────────────────────────────────────────────────────────────────

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const TZ_HOURS = {
  UTC: 0, GMT: 0, Z: 0, UT: 0,
  EST: -5, EDT: -4, CST: -6, CDT: -5, MST: -7, MDT: -6, PST: -8, PDT: -7,
  AKST: -9, AKDT: -8, HST: -10, BST: 1, CET: 1, CEST: 2,
};

// US Pacific offset for a wall-clock time (DST from the 2nd Sunday of March
// to the 1st Sunday of November, 2:00 local), for sheets that only say "PST/PDT".
function pacificOffset(y, mo, d, h) {
  const nthSunday = (month, n) => {
    const first = new Date(Date.UTC(y, month, 1)).getUTCDay();
    return 1 + ((7 - first) % 7) + (n - 1) * 7;
  };
  const start = nthSunday(2, 2), end = nthSunday(10, 1);
  const after = mo > 2 || (mo === 2 && (d > start || (d === start && h >= 2)));
  const before = mo < 10 || (mo === 10 && (d < end || (d === end && h < 2)));
  return after && before ? -7 : -8;
}

function offsetHours(zone, sheetZone, y, mo, d, h) {
  const z = (zone || '').toUpperCase().replace(/[[\]()]/g, '');
  if (z) {
    if (z in TZ_HOURS) return TZ_HOURS[z];
    const m = z.match(/^([+-])(\d{2}):?(\d{2})$/);
    if (m) return (m[1] === '-' ? -1 : 1) * (Number(m[2]) + Number(m[3]) / 60);
  }
  const s = (sheetZone || '').toUpperCase();
  if (/PST|PDT|PACIFIC/.test(s)) return pacificOffset(y, mo, d, h);
  if (/UTC|GMT/.test(s)) return 0;
  return null; // unknown: caller treats the time as UTC and says so
}

function toEpoch(y, mo, d, h, mi, s, ms, zone, sheetZone) {
  if (y < 100) y += 2000;
  const off = offsetHours(zone, sheetZone, y, mo, d, h);
  const t = Date.UTC(y, mo, d, h, mi, s, ms) - (off || 0) * 3600000;
  return isNaN(t) ? null : { t, assumedUtc: off === null };
}

// Parses the time formats seen in provider returns. Returns
// { t: epoch ms, dateOnly, assumedUtc } or null. `zoneHint` is the sheet's
// stated time zone ("UTC", "PST/PDT") for values that don't carry their own.
export function parseTime(raw, zoneHint) {
  if (!raw) return null;
  const s = String(raw).trim().replace(/^timestamp=/i, '').replace(/^"|"$/g, '');
  const ms = frac => (frac ? Math.round(Number(`0.${frac}`) * 1000) : 0);
  let m;
  // 2024-10-05 13:45:02.123 PDT · 2024-10-05T13:45:02Z[GMT] · 2024-10-05 13:45:02 · 2024-10-05T13:45:02+00:00
  if ((m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?\s*(Z|[A-Z]{2,4}|[+-]\d{2}:?\d{2})?\s*(\[[A-Z]+\])?$/))) {
    const r = toEpoch(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0), ms(m[7]), m[8] || m[9], zoneHint);
    return r && { ...r, dateOnly: m[4] === undefined };
  }
  // Thu Oct 05 13:45:02.123456 PDT 2024
  if ((m = s.match(/^(?:[A-Za-z]{3},? )?([A-Za-z]{3}) (\d{1,2}) (\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))? ([A-Z]{2,4}) (\d{4})$/)) && m[1].toLowerCase() in MONTHS) {
    const r = toEpoch(+m[8], MONTHS[m[1].toLowerCase()], +m[2], +m[3], +m[4], +m[5], ms(m[6]), m[7], zoneHint);
    return r && { ...r, dateOnly: false };
  }
  // Tue, 26 Sep 23 14:30(PDT) · Fri, 5 Oct 2024 13:45:02 GMT · Mon, 7 Oct 2024 08:01:02 +0000 (UTC)
  if ((m = s.match(/^(?:[A-Za-z]{3},? )?(\d{1,2}) ([A-Za-z]{3})[a-z]* (\d{2,4}),? (\d{1,2}):(\d{2})(?::(\d{2}))?\s*\(?([A-Z]{2,4}|[+-]\d{4})?\)?/)) && m[2].toLowerCase() in MONTHS) {
    const r = toEpoch(+m[3], MONTHS[m[2].toLowerCase()], +m[1], +m[4], +m[5], +(m[6] || 0), 0, m[7], zoneHint);
    return r && { ...r, dateOnly: false };
  }
  // 10/5/2024 1:45:02 PM UTC · 10/05/2024 13:45
  if ((m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})[ ,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?\s*([A-Z]{2,4}|[+-]\d{2}:?\d{2})?$/i))) {
    let h = +m[4];
    if (m[7]) h = (h % 12) + (/pm/i.test(m[7]) ? 12 : 0);
    const r = toEpoch(+m[3], +m[1] - 1, +m[2], h, +m[5], +(m[6] || 0), 0, m[8], zoneHint);
    return r && { ...r, dateOnly: false };
  }
  // 10-05-2024 or 10/05/2024 (month first, as US exports use)
  if ((m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/))) {
    const r = toEpoch(+m[3], +m[1] - 1, +m[2], 0, 0, 0, 0, '', zoneHint);
    return r && { ...r, dateOnly: true };
  }
  return null;
}

// ─── Time zones for display ──────────────────────────────────────────────────

const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const formatters = {};
function formatterFor(tz) {
  if (!formatters[tz]) {
    formatters[tz] = new Intl.DateTimeFormat('en-US', {
      timeZone: tz === 'local' ? undefined : tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short',
    });
  }
  return formatters[tz];
}

// Wall-clock parts of an instant in a time zone ('local' = the browser's).
// Cached per 15 minutes, which is exact for every zone offset in use.
const partsCache = new Map();
export function zonedParts(t, tz) {
  const key = `${tz}|${Math.floor(t / 900000)}`;
  let p = partsCache.get(key);
  if (!p) {
    const o = Object.fromEntries(formatterFor(tz).formatToParts(new Date(t)).map(x => [x.type, x.value]));
    p = { y: +o.year, mo: +o.month, d: +o.day, h: +o.hour % 24, wd: WEEKDAYS[o.weekday], day: `${o.year}-${o.month}-${o.day}` };
    if (partsCache.size > 200000) partsCache.clear();
    partsCache.set(key, p);
  }
  return { ...p, mi: new Date(t).getUTCMinutes() };
}

// "2026-10-05T22:30" read as wall-clock time in tz → epoch ms.
export function zonedToEpoch(value, tz) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  if (tz === 'local') return new Date(y, mo - 1, d, h, mi).getTime();
  const want = Date.UTC(y, mo - 1, d, h, mi);
  let t = want;
  for (let i = 0; i < 3; i++) {
    const p = zonedParts(t, tz);
    t += want - Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  }
  return t;
}

// ─── Pattern of life and gaps ────────────────────────────────────────────────

export function patternOfLife(events, tz) {
  const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
  const perDay = new Map();
  for (const e of events) {
    if (!e.activity) continue;
    const p = zonedParts(e.t, tz);
    grid[p.wd][p.h]++;
    perDay.set(p.day, (perDay.get(p.day) || 0) + 1);
  }
  const max = Math.max(1, ...grid.flat());
  const days = [...perDay.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1)).map(([day, count]) => ({ day, count }));
  return { grid, max, days, total: grid.flat().reduce((a, b) => a + b, 0) };
}

// Stretches with no activity at all, longer than minHours.
export function activityGaps(events, minHours) {
  const act = events.filter(e => e.activity).sort((a, b) => a.t - b.t);
  const gaps = [];
  for (let i = 1; i < act.length; i++) {
    const ms = act[i].t - act[i - 1].t;
    if (ms >= minHours * 3600000) {
      gaps.push({ from: act[i - 1].t, to: act[i].t, hours: ms / 3600000, before: act[i - 1].source, after: act[i].source });
    }
  }
  return gaps;
}

// ─── Incident window ─────────────────────────────────────────────────────────

export function incidentWindow(events, center, hours) {
  const from = center - hours * 3600000, to = center + hours * 3600000;
  const sorted = [...events].sort((a, b) => a.t - b.t);
  const inWin = sorted.filter(e => e.t >= from && e.t <= to && !e.dateOnly);
  const count = (list, key) => {
    const m = new Map();
    list.forEach(x => [].concat(key(x)).filter(Boolean).forEach(k => m.set(k, (m.get(k) || 0) + 1)));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };
  return {
    from, to, events: inWin,
    ips: count(inWin, e => e.ips || []), handles: count(inWin, e => e.handle), sources: count(inWin, e => e.source),
    models: count(inWin, e => e.models || []), places: count(inWin, e => e.places || []), accounts: count(inWin, e => e.account),
    before: [...sorted].reverse().find(e => e.t < from && e.activity) || null,
    after: sorted.find(e => e.t > to && e.activity) || null,
  };
}

// ─── Night / workday profiles (IPs, cell sites) ──────────────────────────────

// For every value keysOf(event) returns (IPs, cell IDs…): how often, on how many
// days, on how many nights (22:00–06:00, counted against the evening it started)
// and workdays (Mon–Fri 09:00–17:00), in time zone tz.
export function nightWorkProfile(events, tz, keysOf) {
  const map = new Map();
  for (const e of events) {
    if (!e.activity) continue;
    for (const raw of keysOf(e) || []) {
      if (!raw) continue;
      const key = String(raw).toLowerCase();
      if (!map.has(key)) {
        map.set(key, { key: String(raw), count: 0, first: e.t, last: e.t,
          days: new Set(), nights: new Set(), workdays: new Set(), sources: new Set(), accounts: new Set() });
      }
      const x = map.get(key);
      const p = zonedParts(e.t, tz);
      x.count++;
      x.first = Math.min(x.first, e.t);
      x.last = Math.max(x.last, e.t);
      x.days.add(p.day);
      if (p.h >= 22) x.nights.add(p.day);
      if (p.h < 6) x.nights.add(zonedParts(e.t - 6 * 3600000, tz).day);
      if (p.wd >= 1 && p.wd <= 5 && p.h >= 9 && p.h < 17) x.workdays.add(p.day);
      x.sources.add(e.source);
      x.accounts.add(e.account);
    }
  }
  return [...map.values()].map(x => ({
    ...x, days: x.days.size, nights: x.nights.size, workdays: x.workdays.size,
    nightDays: [...x.nights], workDays: [...x.workdays],
    sources: [...x.sources], accounts: [...x.accounts],
  }));
}

// IPv6 addresses on the same home or office network share their first 64 bits.
export function ipv6Network(ip) {
  const s = String(ip).toLowerCase().split('%')[0];
  if (!s.includes(':')) return '';
  const [head, tail = ''] = s.split('::');
  const a = head ? head.split(':') : [], b = tail ? tail.split(':') : [];
  const full = s.includes('::') ? [...a, ...new Array(Math.max(0, 8 - a.length - b.length)).fill('0'), ...b] : a;
  return `${full.slice(0, 4).map(x => x.replace(/^0+(?=.)/, '')).join(':')}::/64`;
}

export function ipProfile(events, tz) {
  return nightWorkProfile(events, tz, e => (e.ips || []).filter(isIp))
    .map(x => ({ ...x, ip: x.key, v6: x.key.includes(':'), network: ipv6Network(x.key) }));
}

// The value (or group: an IPv6 network) seen on the most different nights / workdays,
// if on 3 or more. groupOf(row) → [groupKey, label].
export function homeAndWork(profile, groupOf = x => [x.network || x.ip || x.key, x.network || x.ip || x.key]) {
  const byGroup = new Map();
  for (const x of profile) {
    const [key, label] = groupOf(x);
    if (!byGroup.has(key)) byGroup.set(key, { key, label, ips: [], count: 0, nights: new Set(), work: new Set() });
    const g = byGroup.get(key);
    g.ips.push(x.ip || x.key);
    g.count += x.count;
    x.nightDays.forEach(n => g.nights.add(n));
    x.workDays.forEach(n => g.work.add(n));
  }
  const groups = [...byGroup.values()].map(g => ({ ...g, nightsN: g.nights.size, workN: g.work.size }));
  const best = (field, exclude) => groups
    .filter(g => g.key !== exclude && g[field] >= 3)
    .sort((a, b) => b[field] - a[field] || b.count - a.count)[0] || null;
  const home = best('nightsN');
  const work = best('workN', home && home.key);
  const out = g => g && { label: g.label, ips: g.ips, nights: g.nightsN, workdays: g.workN, count: g.count };
  return { home: out(home), work: out(work) };
}

export function ipv6Networks(profile) {
  const map = new Map();
  for (const x of profile.filter(p => p.network)) {
    if (!map.has(x.network)) map.set(x.network, { network: x.network, addresses: 0, count: 0, nightSet: new Set(), first: x.first, last: x.last, accounts: new Set() });
    const n = map.get(x.network);
    n.addresses++;
    n.count += x.count;
    x.nightDays.forEach(d => n.nightSet.add(d));
    n.first = Math.min(n.first, x.first);
    n.last = Math.max(n.last, x.last);
    x.accounts.forEach(a => n.accounts.add(a));
  }
  return [...map.values()].map(({ nightSet, ...n }) => ({ ...n, nights: nightSet.size, accounts: [...n.accounts] }));
}

// ─── Contacts ────────────────────────────────────────────────────────────────

// Every handle (the other party) with how many records, on how many different
// days, first/last, services and which accounts. ownKeys: Map(account → Set of
// that account's own handleKeys), to set lookups/messages of itself apart.
export function contactRanking(events, ownKeys = new Map(), serviceOf = e => e.service || e.source, tz = 'UTC') {
  const map = new Map();
  for (const e of events) {
    if (!e.handle) continue;
    const key = handleKey(e.handle);
    if (!key) continue;
    if (!map.has(key)) map.set(key, { key, handle: e.handle, count: 0, first: e.t, last: e.t, days: new Set(), services: new Set(), accounts: new Set(), perDay: new Map(), in: 0, out: 0 });
    const x = map.get(key);
    const day = zonedParts(e.t, tz).day;
    x.count++;
    x.first = Math.min(x.first, e.t);
    x.last = Math.max(x.last, e.t);
    x.days.add(day);
    x.perDay.set(day, (x.perDay.get(day) || 0) + 1);
    x.services.add(serviceOf(e));
    x.accounts.add(e.account);
    if (e.direction === 'incoming') x.in++;
    if (e.direction === 'outgoing') x.out++;
  }
  return [...map.values()].map(x => {
    const accts = [...x.accounts];
    return {
      ...x, days: x.days.size, services: [...x.services].filter(Boolean), accounts: accts,
      own: accts.some(a => ownKeys.get(a)?.has(x.key)),
      perDay: [...x.perDay.entries()].sort(),
    };
  });
}

// ─── Links between accounts ──────────────────────────────────────────────────

// typed: { ips: [{label, accounts}], networks: [...], devices: [...], contacts: [...] }.
// Returns one row per pair of target accounts that share anything, strongest first.
export function sharedLinks(targets, typed) {
  const pairs = new Map();
  for (const [type, items] of Object.entries(typed)) {
    for (const { label, accounts } of items || []) {
      const accts = [...new Set(accounts)].filter(a => targets.includes(a)).sort();
      for (let i = 0; i < accts.length; i++) {
        for (let j = i + 1; j < accts.length; j++) {
          const k = `${accts[i]}\u0000${accts[j]}`;
          if (!pairs.has(k)) pairs.set(k, { a: accts[i], b: accts[j], ips: [], networks: [], devices: [], identifiers: [], contacts: [] });
          (pairs.get(k)[type] = pairs.get(k)[type] || []).push(label);
        }
      }
    }
  }
  return [...pairs.values()]
    .map(p => ({ ...p, score: p.devices.length * 10 + p.identifiers.length * 8 + p.networks.length * 3 + p.ips.length * 2 + p.contacts.length }))
    .sort((x, y) => y.score - x.score);
}

// ─── Histories (IMEIs, devices, names…) ──────────────────────────────────────

// Each distinct value of valueOf(event) per account, with first/last seen and count —
// e.g. which IMEIs a phone number was used in, and when it moved to a new one.
export function valueHistory(events, valueOf) {
  const map = new Map();
  for (const e of events) {
    const v = valueOf(e);
    if (!v) continue;
    const k = `${e.account}\u0000${v}`;
    if (!map.has(k)) map.set(k, { account: e.account, value: v, count: 0, first: e.t, last: e.t });
    const x = map.get(k);
    x.count++;
    x.first = Math.min(x.first, e.t);
    x.last = Math.max(x.last, e.t);
  }
  return [...map.values()].sort((a, b) => (a.account < b.account ? -1 : a.account > b.account ? 1 : a.first - b.first));
}

// ─── New-IP check for account events ─────────────────────────────────────────

// For account-security style events: was this IP new to the account? "New"
// means no activity from it more than 24 hours before the event.
export const NEW_IP_WINDOW = 24 * 3600000;
export function newIpChecker(events) {
  const first = new Map();
  for (const e of events) {
    if (!e.activity) continue;
    for (const ip of e.ips || []) {
      const k = `${e.account}|${String(ip).toLowerCase()}`;
      const x = first.get(k) || { first: e.t, count: 0 };
      x.first = Math.min(x.first, e.t);
      x.count++;
      first.set(k, x);
    }
  }
  return (account, ip, t) => {
    if (!ip) return { newIp: false, seen: 0 };
    const p = first.get(`${account}|${String(ip).toLowerCase()}`);
    return { newIp: !p || p.first >= t - NEW_IP_WINDOW, seen: p ? p.count : 0 };
  };
}

// Each time a value (an IMEI, a device) differs from the one on the account's
// previous record: when the phone in use changed, and whether it went back.
export function valueChanges(events, valueOf) {
  const last = new Map(), seen = new Map(), out = [];
  for (const e of [...events].sort((a, b) => a.t - b.t)) {
    const v = valueOf(e);
    if (!v) continue;
    const prev = last.get(e.account);
    if (!seen.has(e.account)) seen.set(e.account, new Set());
    if (prev && prev.value !== v) {
      out.push({ account: e.account, t: e.t, prevT: prev.t, from: prev.value, to: v, back: seen.get(e.account).has(v) });
    }
    seen.get(e.account).add(v);
    last.set(e.account, { value: v, t: e.t });
  }
  return out;
}
